import React from "react";
export const educationContent = {
  sections: [
    {
      title: "Modeling and Simulations",
      id: 1,
      contentRange: [1, 2, 3, 4, 5],
    },
    {
      title: "Modeling Developmental Processes",
      id: 2,
      contentRange: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
    },
    {
      title: "Growing Tissue",
      id: 3,
      contentRange: [ 18, 19, 20, 21],
    },
  ],
  pages: [
    {
      name: "Preface",
      id: 1,
      content: [
        [
          {
            type: "text",
            content:
              "Modeling and simulations are recognized as powerful tools for explaining observations and experimental data, and for making predictions about future trends. This applies to areas as diverse as meteorology, economics, and engineering.",
          },
          {
            type: "link",
            isButton: false,
            linkAnchors: [
              "Neurosphere Simulator",
              "research",
              "Laboratory of Neurobiology at Northeastern University",
            ],
            links: [
              "neurosphere",
              "publications",
              "http://www.northeastern.edu/neurobiology/",
            ],
            beforeContent: [
              "In developmental neurobiology, modeling and simulations are still used rather rarely. By making available a ",
              "based on ",
              "",
            ],
            afterContent: [
              " ",
              " at the ",
              ", our goal is to demonstrate how these approaches can help not just theoretical biologists but also experimentalists to develop a deeper understanding of some important biological phenomena, such as mitosis, apoptosis, cellular differentiation, tumorigenesis, and – at a global level – tissue growth.",
            ],
          },
        ],
      ],
    },
    {
      name: "Two types of modeling frameworks",
      id: 2,
      content: [
        [
          {
            type: "text",
            content: "There are two major types of modeling frameworks:",
          },
          {
            type: "bold",
            beforeContent: ["• "],
            afterContent: [
              " are characterized by continuous time, space, and state. They frequently involve differential equations of the reaction-diffusion type. Their operation is restricted to macroscopic dimensions.",
            ],
            boldWords: ["Continuum models"],
          },
          {
            type: "bold",
            beforeContent: ["• "],
            afterContent: [
              " are characterized by their discrete nature in time, space, and state. They operate in microscopic dimensions, but, like continuum models, enable investigators to make predictions about global outcomes.",
            ],
            boldWords: ["Discrete models"],
          },
          {
            type: "bold",
            beforeContent: [
              "The Neurosphere Simulator presented here is based on ",
              " (simply referred to as ",
            ],
            afterContent: ["", ") modeling, a discrete modeling framework."],
            boldWords: ["cellular automata", "CA"],
          },
        ],
      ],
    },
    {
      name: "Brief history of CA modeling",
      id: 3,
      content: [
        [
          {
            type: "bold",
            beforeContent: [
              "The concept of CA was first proposed by ",
              "in collaboration with ",
            ],
            afterContent: [
              "",
              " in the late 1940s. The goal of von Neumann’s research was to construct a self-replicating automaton that, like biological organisms, can create an identical copy of itself. However, it took until the 1990s that his CA model was first implemented on a computer.",
            ],
            boldWords: ["John von Neumann, ", "Stanislaw Ulam, "],
          },
          {
            type: "image",
            src: "neumann.png",
            alt: "Portrait of John von Neumann in the 1940s.",
            caption:
              "John von Neumann in the 1940s.\n(Photograph courtesy of Los Alamos National Laboratory.)",
          },
        ],
      ],
    },
    {
      name: "General structure of CA: lattice and agents",
      id: 4,
      content: [
        [
          {
            type: "bold",
            beforeContent: [
              "The players in CA models are called ",
              "They occupy finite spaces on a ",
            ],
            afterContent: [
              ". ",
              ". In most applications, this lattice is two-dimensional and consists of squares, although sometimes a hexagonal structure is used.",
            ],
            boldWords: ["agents", "lattice"],
          },
          {
            type: "image",
            src: "Lattice_and_agents.png",
            alt:
              "Orange-colored circle surrounded by red-colored square at the center of gray-colored grid.",
            caption:
              "An agent (orange) occupying a lattice site (red) at location (x,y) of a square lattice (gray).",
          },
        ],
      ],
    },
    {
      name: "General structure of CA: rules and iteration",
      id: 5,
      content: [
        [
          {
            type: "custom",
            content: (
              <p>
                Agents interact with neighboring agents by executing specific
                <b> rules</b> that are applied at each discrete time step
                (referred to as <b>iteration</b> <i>t</i>). After each
                iteration, the configuration of the system is updated.
              </p>
            ),
          },
          {
            type: "text",
            content:
              "We will learn more about rules in the next section when we describe how CA modeling can be applied to developmental neurobiology.",
          },
        ],
      ],
    },
    {
      name: "General structure of CA: interactions between neighbors",
      id: 6,
      content: [
        [
          {
            type: "bold",
            beforeContent: ["A popular type of neighborhood is the "],
            afterContent: [" shown here."],
            boldWords: ["von Neumann neighborhood"],
          },
          {
            type: "image",
            src: "Neighborhood.png",
            alt:
              "Grid with 5 squares colored blue, but the center lattice site shaded red, within a larger gray grid.",
            caption:
              "The von Neumann neighborhood (blue) of a lattice site (red) at location (x,y) of a square lattice (gray).",
          },
        ],
      ],
    },
    {
      name: "Cell proliferation",
      id: 7,
      content: [
        [
          {
            type: "text",
            content:
              "Let’s start this tutorial with the most fundamental process of development: cell proliferation. To initialize the simulation, we first place a stem cell (red) in the center of the lattice.",
          },
          {
            type: "image",
            src: "01_cell_proliferation.png",
            alt: "5x5 grid with single red stem cell placed into its center.",
            caption:

              "5x5 square lattice with single stem cell placed into its center.",

          },
        ],
      ],
    },
    {
      name: "Mitosis",
      id: 8,
      content: [
        [
          {
            type: "text",
            content:
              "Before we instruct the stem cell to divide, let’s think about what might happen.",
          },
          {
            type: "tooltip",
            beforeContent: [
              "When a cell (we call it the mother cell) undergoes ",
            ],
            activeWords: ["mitosis"],
            tooltips: [
              "Mitosis: Cell division resulting in two genetically identical daughter cells",
            ],
            afterContent: [
              ", it will divide into two daughter cells. Thus, the two new cells will require roughly twice as much space as one cell does (‘roughly’ because immediately after their birth cells are usually smaller than they are later in their life).",
            ],
          },
          {
            type: "image",
            src: "cell_proliferation.gif",
            alt:
              "5x5 grid, first with red circle in center, then two red circles appear.",
            caption:
              "A simulated mother cell divides to generate two daughter cells.",
          },
        ],
        [
          {
            type: "image",
            src: "cell_proliferation.gif",
            alt:
              "5x5 grid, first with red circle in center, then two red circles appear.",
            caption:
              "A simulated mother cell divides to generate two daughter cells.",
          },
          {
            type: "text",
            content:
              "In the simulation, this physical space requirement is reflected by the number of lattice sites taken by the cell(s): Before division, one lattice site is occupied; after division, two lattice sites are occupied. The two (biological) daughter cells often remain, at least initially, in close proximity. The model has, therefore, been built in a way that the two simulated daughter cells that have resulted from the mitotic division of the mother cell occupy two neighboring lattice positions.",
          },
        ],
      ],
    },
    {
      name: "Daughter position selection",
      id: 9,
      content: [
        [
          {
            type: "image",
            src: "02_cell_proliferation.png",
            alt:
              "Final scene of previous video clip: two daughter cells positioned on a 5x5 lattice after mitosis.",
            caption: null,
          },
          {
            type: "text",
            content:
              "In the example shown, after division, one of the daughter cells (labeled ‘1’) is situated at the site formerly occupied by the mother cell — this is how the model has been constructed.",
          },
          {
            type: "text",
            content:
              "However, can one predict on which of the four neighboring lattice sites the other daughter cell (labeled ‘2’) will end up?",
          },
        ],
        [
          {
            type: "text",
            content:
              "To answer this question, let’s run some simulations. We will instruct the cell to divide twenty times and pay attention to which neighboring lattice site contains the other daughter cell.",
          },
          {
            type: "text",
            content:
              " Click next and then watch the animation to see what happens.",
          },
          {
            type: "image",
            src: "04_20_simulation.gif",
            alt:
              "Video of 20 simulations to showcase random selection of second daughter cell spot. Outcome described on next page.",
            caption: null,
          },
        ],
        [
          {
            type: "text",
            content:
              "To better visualize the result, we can plot a frequency distribution histogram.",
          },
          {
            type: "image",
            src: "lattice_placement_histogram.png",
            alt:
              "Frequency distribution histogram of daughter cell placement after simulation of 20 stem cell divisions.",
            caption:
              "Frequency distribution histogram of daughter cell placement after simulation of 20 stem cell divisions.",
          },
          {
            type: "text",
            content:
              "What do the results tell us? Out of the twenty simulations, neighbor position 1 was occupied four times, neighbor position 2 seven times, neighbor position 3 five times, and neighbor position 4 four times.",
          },
        ],
        [
          {
            type: "text",
            content:
              "The reason for this outcome is, again, determined by the algorithm used to construct the model. This particular detail of the algorithm is shown in the following flow chart:",
          },
          {
            type: "image",
            src: "flowchart_division.png",
            alt:
              "Flow chart of algorithm that defines stem cell division and placement of daughter cells. A starting stem cell divides with probability P_m (probability of mitosis) to produce two daughter cells. The arrangement of the daughter cells is chosen with probability P_DA, which is equal for each of the four possible positions (one fourth).",
            caption:
              "Flow chart of algorithm that defines stem cell division and placement of daughter cells.",
          },
        ],
        [
          {
            type: "image",
            src: "flowchart_division.png",
            alt:
              "Flow chart of algorithm that defines stem cell division and placement of daughter cells. A starting stem cell divides with probability P_m (probability of mitosis) to produce two daughter cells. The arrangement of the daughter cells is chosen with probability P_DA, which is equal for each of the four possible positions (one fourth).",
            caption:
              "Flow chart of algorithm that defines stem cell division and placement of daughter cells.",
          },
          {
            type: "text",
            content:
              "As you can see, after initiation of mitosis, a certain probability is assigned with which each neighbor site will be occupied. In our case, this probability is P = 0.25, or in common terms, 25%.",
          },
          {
            type: "tooltip",
            beforeContent: [
              "In other words: while the selection of the central position by one of the two daughter cells is a ",
              "the selection of the position for the second daughter cell is a ",
            ],
            activeWords: ["deterministic process", "stochastic process"],
            tooltips: [
              "Deterministic process: a process whose outcome can be predicted with absolute certainty.",
              "Stochastic process: a process whose outcome is subject to random variables.",
            ],
            afterContent: [
              ", ",
              ". This means that each of the four neighbor sites has the same chance to be ‘selected’ by one of the two daughter cells. Of course, this does not imply that, if we repeat this process four times, each lattice position will be taken exactly once.",
            ],
          },
        ],
      ],
    },
    {
      name: "Properties of stem cells",
      id: 10,
      content: [
        [
          {
            type: "text",
            content:
              "The two defining properties of stem cells are that they can: \n• divide indefinitely to produce clones of themselves;\n• give rise to cells that differentiate.",
          },
          {
            type: "bold",
            beforeContent: [
              "The ability of stem cells to make more stem cells is known as ",
            ],
            afterContent: ["."],
            boldWords: ["self-renewal"],
          },
          {
            type: "bold",
            beforeContent: [""],
            afterContent: [
              " refers to the process during which a less specialized cell develops into a more specialized cell, for example a neuron.",
            ],
            boldWords: ["Cellular differentiation"],
          },
        ],
      ],
    },
    {
      name: "Progenitor and differentiated cells",
      id: 11,
      content: [
        [
          {
            type: "bold",
            beforeContent: [
              "Stem cells frequently ",
              " As a result, one daughter cell is a clone of the mother cell, whereas the other daughter is more specialized. Often, the latter cell (referred to as ",
              " will undergo a limited number of cell divisions, until the final mitosis yields a ",
            ],
            afterContent: [".", ")", "."],
            boldWords: [
              "divide asymmetrically",
              "progenitor",
              "differentiated cell",
            ],
          },
          {
            type: "text",
            content:
              "This process can be modeled by defining, in addition to stem cells, progenitor cells (orange) and differentiated cells (blue).",
          },
          {
            type: "image",
            src: "cell_types.png",
            alt:
              "Three 3x3 lattices containing a single cell. The first is a stem cell (colored red); the second is a progenitor cell (colored orange); the third is a differentiated cell (colored blue).",
            caption:
              "Three simulated cell types — stem cell, progenitor, and differentiated cell.",
          },
        ],
      ],
    },
    {
      name: "Proliferative potential of cells",
      id: 12,
      content: [
        [
          {
            type: "bold",
            beforeContent: [
              "In CA models, we can distinguish the three cell types by assigning to them different ",
            ],
            afterContent: [
              ":\n• stem cell: indefinite proliferative potential\n• progenitor cell: limited proliferative potential\n• differentiated cell: no proliferative potential",
            ],
            boldWords: ["proliferative potentials"],
          },
        ],
        [
          {
            type: "text",
            content:
              "Let’s set the proliferative potential of the progenitor to 2 and simulate an asymmetric division of a stem cell.",
          },
          {
            type: "image",
            src: "asymmetric_division.gif",
            alt:
              "Asymmetric division of a stem cell generates a progenitor cell that, via two daughter progenitors, gives rise to four differentiated cells.",
            caption: "Asymmetric division of a stem cell.",
          },
          {
            type: "text",
            content:
              "The initial daughter progenitor gives rise to two progenitor cells. Each of them produces, as a result of their division, two differentiated cells, i.e. a total of four differentiated cells are generated.",
          },
        ],
        [
          {
            type: "bold",
            beforeContent: ["Progenitor cells are also called "],
            afterContent: [". They have a profound effect on tissue growth."],
            boldWords: ["transit amplifying cells"],
          },
          {
            type: "text",
            content:
              "Usually, the effect of progenitors on tissue growth is greater the greater their proliferative potential is. In the Neurosphere Simulator, the user can change the proliferative potential of progenitor cells. Look out for the following input box:",
          },
          {
            type: "image",
            src: "proliferative_potential.png",
            alt:
              "Part of the dashboard that allows users to change the proliferative potential.",
            caption: null,
          },
        ],
      ],
    },
    {
      name: "Progenitor cell division",
      id: 13,
      content: [
        [
          {
            type: "text",
            content:
              "In the CA model, on which the Neurosphere Simulator is based, the progenitors are subject to similar rules as are the stem cells. For example, the cells (progenitors or differentiated cells) produced by mitotic division of a progenitor cell will occupy grid positions deterministically (one of the cells will replace the mother cell) and stochastically (the other cell will choose between vacant neighbor positions with equal probability). Such a situation is shown below:",
          },
          {
            type: "image",
            src: "simple_progenitor_division.gif",
            alt:
              "Mitotic division of a progenitor cell gives rise to two daughter progenitors.",
            caption:
              "Mitotic division of a progenitor cell gives rise to two daughter progenitors.",
          },
        ],
      ],
    },
    {
      name: "Contact inhibition",
      id: 14,
      content: [
        [
          {
            type: "text",
            content:
              "So far, we have considered only situations in which a mother (stem or progenitor) cell is surrounded by the theoretically maximum number of vacant positions (four in case of the lattice types used here). What, however, happens if all of the neighbor sites are occupied by another cell, as shown here?",
          },
          {
            type: "image",
            src: "stem_cell_four_neighbors.png",
            alt:
              "Stem cell in a 5x5 lattice with all four surrounding grid positions occupied.",
            caption:
              "Stem cell surrounded by four neighboring differentiated cells.",
          },
          {
            type: "text",
            content: "In such a case, mitosis is completely inhibited.",
          },
        ],
        [
          {
            type: "bold",
            beforeContent: [
              "Obviously, two simulated cells cannot occupy the same lattice position at the same time — just like two biological cells cannot occupy the same physical space at the same time. Furthermore, research has shown that the probability of cell division in tissue may be regulated (among other factors) by the density of cells in the tissue. If the density is high and no adequate physical space is available for the two daughter cells, mitotic division may be completely suppressed. This phenomenon is called ",
            ],
            afterContent: ["."],
            boldWords: ["contact inhibition"],
          },
        ],
        [
          {
            type: "text",
            content:
              "To accommodate for this observation, in our CA model the probability of cell division is regulated by the number of neighbors in the local neighborhood of a stem or progenitor cell. The larger the number of neighbors, the less likely this cell is to divide.",
          },
          {
            type: "image",
            src: "stem_cell_two_neighbors.png",
            alt:
              "Stem cell in a 5x5 lattice surrounded by two differentiated cells.",
            caption:
              "Stem cell surrounded by two neighboring differentiated cells.",
          },
          {
            type: "text",
            content:
              "In the case shown in the figure, mitosis is possible, but its probability is lower than if only one grid position is occupied (or if none are occupied).",
          },
        ],
        [
          {
            type: "text",
            content:
              "The following flow chart shows the part of the algorithm that sets the rules for the probability of a cell to undergo mitosis. By implementing an IF condition:",
          },
          {
            type: "text",
            content:
              "• a probability of 0 is assigned in case of all neighboring grid positions being occupied by other cells;",
          },
          {
            type: "text",
            content:
              "• incrementally increasing probabilities are assigned in case of an increasing number of grid positions in the neighborhood being empty.",
          },
          {
            type: "image",
            src: "Flowchart_Division_with_Contact_Inhibition.png",
            alt:
              "Flow chart demonstrating the probability of a cell to undergo mitosis. The number of empty neighbor cells, N_e, can be 0, 1, 2, 3, or 4. A starting stem cell divides with probability P_m(N_e) (function of the probability of mitosis and the number of empty neighbor cells) to produce two daughter cells. The arrangement of the daughter cells is chosen with probability P_DA, which is equal for each of the empty neighbor cell sites. If there are four empty sites, the probability of each daughter configuration is one fourth. If there are three empty sites, the probability of each daughter configuration is one third, and so on.",
            caption:
              "Flow chart of algorithm that defines the probability of a stem cell to undergo mitosis, regulated by the number of neighboring grid positions occupied.",
          },
        ],
      ],
    },
    {
      name: "Cell death",
      id: 15,
      content: [
        [
          {
            type: "tooltip",
            beforeContent: [
              "It may seem counterintuitive at first glance, but cell death is closely associated with cell proliferation and tissue growth. During development, cell death is usually apoptotic in nature. Shortly after mitosis of stem cells or progenitors, ",
            ],
            activeWords: ["apoptosis"],
            tooltips: [
              "Apoptosis: Type of cell death that plays an important role in eliminating supernumerary cells during development. <br /> Morphologically characterized by cell shrinkage, generation of membrane-bound cell fragments, and absence of tissue inflammation.",
            ],
            afterContent: [
              " frequently occurs in one of the two daughter cells, whereas the other survives and continues to proliferate or differentiates.",
            ],
          },
          {
            type: "text",
            content:
              "Research suggests that this differential fate of the two daughter cells is regulated by asymmetric distribution of pro- and anti-apoptotic proteins during the final stages of apoptosis.",
          },
          {
            type: "text",
            content:
              "Furthermore, observations in epithelial cells have identified local cell density as a major factor for controlling cell death.",
          },
        ],
        [
          {
            type: "text",
            content:
              "To mimic the biological situation in a CA model, rules are implemented that define\n• which of the two daughter cells will potentially die after a mitotic division;\n• the probability of cell death of the candidate cell as a function of local cell density.",
          },
          {
            type: "text",
            content:
              "In the case of an asymmetrically dividing stem cell, we might want to subject only the resulting progenitor cell, but not the stem cell clone, to the possibility of cell death.",
          },
        ],
        [
          {
            type: "text",
            content:
              "Such a situation in which the progenitor cell dies is shown here:",
          },
          {
            type: "image",
            src: "progenitor_daughter_death.gif",
            alt: "",
            caption:
              "Asymmetric division of a stem cell resulting in a stem cell clone and a progenitor cell. \nThe progenitor cell immediately dies, so that at the end of iteration 1 only the (daughter) stem cell and the dead cell are visible.",
          },
        ],
        [
          {
            type: "text",
            content:
              "In the case of a symmetrically dividing progenitor cell, we might want to define that one of the daughter cells is always destined to survive, whereas the other is subject to the local cell density-dependent probability of cell death. This probability would increase with increasing local cell density. Thus, as shown here, two outcomes are possible when a progenitor cell divides:",
          },
          {
            type: "image",
            src: "progenitor_division.gif",
            alt: "",
            caption:
              "Symmetric mitotic division of a progenitor cell surrounded by three differentiated cells. \nOn the left, two daughter progenitor cells are produced. On the right, one of the two daughter cells dies through apoptosis.",
          },
        ],
      ],
    },
    {
      name: "Phagocytosis",
      id: 16,
      content: [
        [
          {
            type: "tooltip",
            beforeContent: [
              "In biological tissue, cellular debris resulting from cell death is routinely removed through ",
              "",
              "",
            ],
            afterContent: [
              " by ",
              " and ",
              ". The clearance of the cell corpses has an important consequence for the growth of tissue: by reducing contact inhibition and making space available for newly generated cells, cell death, in combination with phagocytosis, acts as a mechanism of developmental plasticity.",
            ],
            activeWords: ["phagocytosis", "macrophages", "microglia"],
            tooltips: [
              "Phagocytosis: Process by which a cell engulfs other cell(s) or cellular particles.",
              "Macrophage: A type of white blood cell involved in phagocytosis as part of an organism’s immune response.",
              "Microglia: A type of glial cells that function as an immune cells in the central nervous system. They are involved in clearance of apoptotic cells through phagocytosis.",
            ],
          },
        ],
        [
          {
            type: "image",
            src: "phagocytosis.png",
            alt:
              "Three 5x5 lattices. In the first lattice, a progenitor cell is surrounded by three differentiated cells and one dead cell. In the second lattice, the dead cell is removed through phagocytosis. In the third lattice, the progenitor cell divides and produces two daughter progenitors.",
            caption:
              "Removal of a dead cell through phagocytosis clears space in the neighborhood, thereby enabling mitosis of the progenitor cell in the center.",
          },
          {
            type: "text",
            content:
              "In the situation depicted here, a progenitor cell is initially surrounded by four neighboring cells — three differentiated cells and one dead cell. Thus, cell proliferation is completely inhibited. However, after the cell corpse has been removed through phagocytosis, the progenitor cell is able to undergo mitosis (with a certain probability). For example, and as shown here, a symmetric division of this progenitor cell yields two daughter progenitor cells.  One of them can now occupy the lattice position at which previously the dead cell was located.",
          },
        ],
      ],
    },
    {
      name: "Transformation of progenitor cells into brain tumor stem cells",
      id: 17,
      content: [
        [
          {
            type: "bold",
            beforeContent: [
              "A fundamental property of cancer is ",
              "tumors arise as clones from a single cell that proliferates abnormally. It is thought that the most frequent type of brain tumors, ",
              "originates from transformation of a progenitor cell into a ",
            ],
            afterContent: [
              ": ",
              ", ",
              " This transformed stem cell, in turn, gives rise to tumorous tissue.",
            ],
            boldWords: [
              "tumor clonality",
              "glioblastoma multiforme",
              "brain tumor stem cell.",
            ],
          },
        ],
        [
          {
            type: "text",
            content:

              "The Neurosphere Simulator offers the possibility of such transformation of a progenitor cell into a brain tumor stem cell (at any iteration number selected by the user). After transformation, the brain tumor stem cell will undergo only symmetric divisions, thus mimicking phases of aggressive growth of tumor tissue.",

          },
          {
            type: "text",
            content:

              "This animation shows the transformation of a progenitor cell into a brain tumor stem cell (green) and subsequent rapid growth of tumorous tissue driven by symmetric division of brain tumor stem cell clones.",

          },
          {
            type: "image",
            src: "BTSC_transformation.gif",
            alt:
              "Progenitor cell quickly differentiates into an assay full of brain tumor stem cells",
            caption: "",
          },
        ],
      ],
    },
    {
      name: "The neurosphere assay: from stem cell to neurosphere",
      id: 18,
      content: [
        [
          {
            type: "tooltip",
            beforeContent: [
              "The Neurosphere Simulator presented here simulates stem-cell-driven tissue growth in a specific in vitro assay system, commonly known as the ",
              "It was established in 1992 by two Canadian scientists, Brent A. Reynolds and Samuel Weiss. The underlying idea is to isolate a single neural stem cell and provide proper ",
              "",
            ],
            afterContent: [
              ". ",
              " so that this stem cell proliferates and grows into a spherical structure, like the one shown here. This spherical structure is called ",
              ". Typically, the spheres assume diameters ranging from 40 μm to 300 μm.",
            ],
            activeWords: [
              "neurosphere assay",
              "culture conditions",
              "neurosphere",
            ],
            tooltips: [
              "BOLD",
              "Culture conditions: Culture medium free of serum but containing epidermal growth factor, fibroblast growth factor 2, or both.",
              "BOLD",
            ],
          },
          {
            type: "image",
            src: "neurosphere.jpg",
            alt: "Photograph of a green and blue neurosphere.",
            caption:
              "Neurosphere composed of neural stem cells (green). Cell nuclei are shown in blue. (Courtesy: Archontia Kaminari.)",
          },
        ],
        [
          {
            type: "text",
            content:
              "The neurosphere assay has proven to be an excellent in vitro system to study the behavior of neural stem cells and their progeny. Since the paper published by Reynolds and Weiss in 1992, the neurosphere assay has been employed in well over one thousand investigations.",
          },
        ],
      ],
    },
    {
      name: "Neurosphere assay: cellular differentiation",
      id: 19,
      content: [
        [
          {
            type: "text",
            content:
              "Withdrawal of the growth factor(s) and addition of serum to the culture medium will induce undifferentiated neurosphere cells to differentiate into three types of cells — neurons, astrocytes, and oligodendrocytes. The ability to give rise to multiple types of cells is a crucial property of stem cells.",
          },
          {
            type: "text",
            content:
              "(The other defining property of stem cells is their ability to self-renew — mitotic divisions yield one clone of the mother cell in the case of asymmetric divisions, and two clones in the case of symmetric divisions.)",
          },
        ],
        [
          {
            type: "text",
            content:
              "Differentiated cells derived from a neurosphere are shown here.",
          },
          {
            type: "image",
            src: "differentiated_cells.png",
            alt: "Photographs of differentiated cells in media.",
            caption:
              "Differentiation of neurosphere cells into glia and neurons. The neurosphere was grown from an adult neural stem cell isolated from the brain of a teleost fish. (A) Astrocyte expressing the astrocytic marker protein glial fibrillary acidic protein (GFAP). (B) Glial cell expressing vimentin. (C) Neuron expressing the neuron-specific protein Hu. (D) Neuron immunopositive for microtubule-associated protein 2 (MAP2 (2a + 2b)). Nuclei were stained with either propidium iodide (red) or DAPI (blue). Calibration bars: 20μm. (From: Hinsch K, Zupanc GKH, J. Neurosci. Methods 158:75-88, 2006.)",
          },
        ],
      ],
    },
    {
      name: "Neurosphere simulation: from 3D to 2D",
      id: 20,
      content: [
        [
          {
            type: "text",
            content:
              "After this brief introduction to biological neurospheres, let’s now turn to neurospheres simulated based on the CA model described in the 'Modeling Developmental Processes' section of this tutorial.",
          },
          {
            type: "text",
            content:
              "In these simulations, we reduce the three dimensions of the biological neurosphere to two dimensions using a two-dimensional lattice. Since neurospheres develop (with some variability) radially outward from the center, the cross sections formed by any two planes passing through the center of the sphere will be similar. Thus, the ‘neurosphere’ structures generated by simulations based on our CA model are essentially representations of such cross-sections.",
          },
        ],
      ],
    },
    {
      name: "Neurosphere simulation: growing the tissue",
      id: 21,
      content: [
        [
          {
            type: "text",
            content:
              "Let’s now demonstrate the simulation of the growth of a neurosphere, using the default settings of the Neurosphere Simulator. For this simulation, a stem cell is placed in the center of a lattice and allowed to undergo asymmetric mitotic divisions, regulated by the cell density in the neighborhood.",
          },
          {

            type: "image",
            src: "tissue_growth_simulation.gif",
            alt:
              "Video of 20 simulations to showcase random selection of second daughter cell spot. Outcome described on next page.",
            caption: "Simulated growth of a neurosphere.",
          },
        ],
      ],
    },
    {
      name: "Explore yourself the power of modeling and simulation",
      id: 22,
      content: [
        [
          {
            type: "link",
            isButton: true,
            linkAnchors: ["Go to Neurosphere Simulator"],
            links: ["neurosphere"],
            beforeContent: [
              "After this brief demonstration, you are now ready to explore yourself how modeling and simulation can help you to dive deeper into the theory of tissue development. Click on the link below to start your journey. Bon voyage!\n\n ",
            ],
            afterContent: [""],
          },
        ],
      ],
    },
  ],
};
